import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { onboardingApi } from '../../api/doctorApi'

// -- Thunks ----------------------------------------------------------------

export const fetchOnboardingData = createAsyncThunk(
  'onboarding/fetchOnboardingData',
  async (_, thunkAPI) => {
    try {
      const data = await onboardingApi.fetch()
      return data
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || 'Failed to fetch onboarding data'
      )
    }
  }
)

export const fetchAvailableOptions = createAsyncThunk(
  'onboarding/fetchAvailableOptions',
  async (_, thunkAPI) => {
    try {
      const data = await onboardingApi.availableOptions()
      return data
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || 'Failed to fetch available options'
      )
    }
  }
)

export const submitStep = createAsyncThunk(
  'onboarding/submitStep',
  async ({ step, data }, thunkAPI) => {
    const state = thunkAPI.getState().onboarding
    const id = state.onboardingData?.id
    if (!id) {
      return thunkAPI.rejectWithValue('Onboarding ID not found')
    }
    try {
      const result = await onboardingApi.submitStep(id, { step, data })
      return result
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || 'Failed to submit step'
      )
    }
  }
)

export const saveDraft = createAsyncThunk(
  'onboarding/saveDraft',
  async ({ step, data }, thunkAPI) => {
    const state = thunkAPI.getState().onboarding
    const id = state.onboardingData?.id
    if (!id) {
      return thunkAPI.rejectWithValue('Onboarding ID not found')
    }
    try {
      const result = await onboardingApi.saveDraft(id, { [step]: data })
      return result
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || 'Failed to save draft'
      )
    }
  }
)

export const resetStep = createAsyncThunk(
  'onboarding/resetStep',
  async ({ step }, thunkAPI) => {
    const state = thunkAPI.getState().onboarding
    const id = state.onboardingData?.id
    if (!id) {
      return thunkAPI.rejectWithValue('Onboarding ID not found')
    }
    try {
      const result = await onboardingApi.resetStep(id, step)
      return result
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || 'Failed to reset step'
      )
    }
  }
)

export const uploadDocument = createAsyncThunk(
  'onboarding/uploadDocument',
  async ({ documentType, file }, thunkAPI) => {
    const state = thunkAPI.getState().onboarding
    const id = state.onboardingData?.id
    if (!id) {
      return thunkAPI.rejectWithValue('Onboarding ID not found')
    }
    const formData = new FormData()
    formData.append('document_type', documentType)
    formData.append('document', file)
    try {
      const result = await onboardingApi.uploadDocument(id, formData)
      return result
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || 'Failed to upload document'
      )
    }
  }
)

export const fetchVerificationStatus = createAsyncThunk(
  'onboarding/fetchVerificationStatus',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState().onboarding
    const id = state.onboardingData?.id
    if (!id) {
      return thunkAPI.rejectWithValue('Onboarding ID not found')
    }
    try {
      const result = await onboardingApi.verificationStatus(id)
      return result
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data || err.message || 'Failed to fetch verification status'
      )
    }
  }
)

// -- Slice ----------------------------------------------------------------

const initialState = {
  onboardingData: null,
  availableOptions: null,
  verificationStatus: null,
  loading: false,
  error: null,
}

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchOnboardingData
      .addCase(fetchOnboardingData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOnboardingData.fulfilled, (state, { payload }) => {
        state.loading = false
        state.onboardingData = payload
      })
      .addCase(fetchOnboardingData.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      // fetchAvailableOptions
      .addCase(fetchAvailableOptions.pending, (state) => {
        state.error = null
      })
      .addCase(fetchAvailableOptions.fulfilled, (state, { payload }) => {
        state.availableOptions = payload
      })
      .addCase(fetchAvailableOptions.rejected, (state, { payload }) => {
        state.error = payload
      })

      // submitStep
      .addCase(submitStep.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(submitStep.fulfilled, (state, { payload }) => {
        state.loading = false
        if (state.onboardingData) {
          state.onboardingData.current_step = payload.current_step
          state.onboardingData.completed_steps = payload.completed_steps
          state.onboardingData.is_completed = payload.is_completed
          state.onboardingData.draft_data = payload.draft_data
        }
      })
      .addCase(submitStep.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      // saveDraft
      .addCase(saveDraft.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(saveDraft.fulfilled, (state, { payload }) => {
        state.loading = false
        if (payload.draft_data && state.onboardingData) {
          state.onboardingData.draft_data = payload.draft_data
          state.onboardingData.current_step = payload.current_step
        }
      })
      .addCase(saveDraft.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      // resetStep
      .addCase(resetStep.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resetStep.fulfilled, (state, { payload }) => {
        state.loading = false
        if (state.onboardingData) {
          state.onboardingData.current_step = payload.current_step
          state.onboardingData.completed_steps = payload.completed_steps
          state.onboardingData.draft_data = payload.draft_data
        }
      })
      .addCase(resetStep.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      // uploadDocument
      .addCase(uploadDocument.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(uploadDocument.fulfilled, (state, { payload }) => {
        state.loading = false
        state.verificationStatus = payload
      })
      .addCase(uploadDocument.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      // fetchVerificationStatus
      .addCase(fetchVerificationStatus.pending, (state) => {
        state.error = null
      })
      .addCase(fetchVerificationStatus.fulfilled, (state, { payload }) => {
        state.verificationStatus = payload
      })
      .addCase(fetchVerificationStatus.rejected, (state, { payload }) => {
        state.error = payload
      })
  },
})

export const { clearError } = onboardingSlice.actions
export default onboardingSlice.reducer
